import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getDatalists } from 'features/datalist';
import { useApiToast } from 'components/Toaster';
import { Person } from 'services/person/person.types';
import { Appointment } from 'services/appointment/appointment.types';
import { useGetProjectAppointmentsMutation, useUpdateAppointmentMutation } from 'services/appointment/appointment.endpoint';

const sortAppointments = (list: Appointment[]) =>
    [...list].sort((a, b) => {
        // les demandes en attente d'abord, puis par date décroissante
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (b.status === 'pending' && a.status !== 'pending') return 1;
        return moment(b.started_at).valueOf() - moment(a.started_at).valueOf();
    });

export function useAppointmentRequestsTabVM(person: Person) {
    const { datalists } = useSelector(getDatalists);
    const { showErrorToast, showApiToast } = useApiToast();
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);

    const projectId = person?.projects?.[0]?.id;

    const [getProjectAppointments] = useGetProjectAppointmentsMutation();
    const [updateAppointment] = useUpdateAppointmentMutation();

    useEffect(() => {
        if (!projectId) {
            setIsLoading(false);
            return;
        }
        (async () => {
            try {
                const res = await getProjectAppointments({ project_id: String(projectId) } as any).unwrap();
                setAppointments(sortAppointments(res.data ?? []));
            } catch (e) {
                // pas de projet / pas de demande
            } finally {
                setIsLoading(false);
            }
        })();
    }, [projectId, getProjectAppointments]);

    const applyStatus = (id: string, updated: Partial<Appointment>) => {
        setAppointments((prev) => sortAppointments(prev.map((a) => (a.id === id ? { ...a, ...updated } : a))));
    };

    const handleAccept = async (appointment: Appointment, started_at: string, ended_at: string) => {
        if (!projectId) return;
        setProcessingId(appointment.id);
        try {
            const res = await updateAppointment({
                id: appointment.id,
                project_id: String(projectId),
                status: 'accepted',
                started_at,
                ended_at
            }).unwrap();
            showApiToast(res, {
                successMessage: 'Rendez-vous confirmé et ajouté au calendrier',
                errorMessage: 'Erreur lors de la confirmation du rendez-vous'
            });
            applyStatus(appointment.id, { status: 'accepted', started_at, ended_at });
        } catch (e: any) {
            showErrorToast(e, { message: 'Erreur lors de la confirmation du rendez-vous' });
        } finally {
            setProcessingId(null);
        }
    };

    const handleRefuse = async (appointment: Appointment) => {
        if (!projectId) return;
        setProcessingId(appointment.id);
        try {
            const res = await updateAppointment({
                id: appointment.id,
                project_id: String(projectId),
                status: 'refused'
            }).unwrap();
            showApiToast(res, {
                successMessage: 'Demande de rendez-vous refusée',
                errorMessage: 'Erreur lors du refus du rendez-vous'
            });
            applyStatus(appointment.id, { status: 'refused' });
        } catch (e: any) {
            showErrorToast(e, { message: 'Erreur lors du refus du rendez-vous' });
        } finally {
            setProcessingId(null);
        }
    };

    return { datalists, appointments, isLoading, processingId, handleAccept, handleRefuse };
}
